import { useCallback, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────────────────────────────────────────────────────
 * useZipLookup — small reusable hook for the "type ZIP → auto-fill
 * City + State + warn on mismatch" UX used by both the Onboarding
 * Setup page and the Dashboard Settings tab.
 *
 * Returns:
 *   resolve(zip, currentState) → Promise<{ valid, city, state, mismatch }>
 *   - valid:    is the ZIP a real US ZIP?
 *   - city:     canonical city from Zippopotam (may be "" on offline fallback)
 *   - state:    USPS 2-letter code resolved for this ZIP
 *   - mismatch: true when `currentState` is set and disagrees with the
 *               resolved state — the caller surfaces an inline error.
 *
 *   warning:    string | "" — current human-readable warning to display
 *               under the ZIP / State fields. Cleared by `clearWarning()`.
 *   loading:    bool — fetch in-flight.
 *
 * Caches in-memory per session so flipping back-and-forth between two
 * ZIPs doesn't re-hit the backend on every keystroke.
 * ─────────────────────────────────────────────────────────────────── */
const CACHE = new Map();

export default function useZipLookup(token) {
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);

  const resolve = useCallback(async (zip, currentState) => {
    const cleaned = String(zip || '').replace(/[^0-9]/g, '').slice(0, 5);
    if (cleaned.length !== 5) {
      setWarning('');
      return { valid: false, city: '', state: '', mismatch: false };
    }
    let data = CACHE.get(cleaned);
    if (!data) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/address/zip-lookup/${cleaned}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        data = res.data;
        CACHE.set(cleaned, data);
      } catch (err) {
        // Soft-fail: leave the caller to handle missing data. The PUT-time
        // server validator will still block bad ZIPs.
        setLoading(false);
        return { valid: false, city: '', state: '', mismatch: false };
      } finally {
        setLoading(false);
      }
    }
    if (!data?.valid) {
      setWarning('Please enter a valid U.S. ZIP Code.');
      return { valid: false, city: '', state: '', mismatch: false };
    }
    const expected = (data.state || '').toUpperCase();
    const provided = (currentState || '').toUpperCase();
    const mismatch = !!(provided && expected && provided !== expected);
    if (mismatch) {
      setWarning(`ZIP Code ${cleaned} belongs to ${expected}, not ${provided}. Please correct the State before saving.`);
    } else {
      setWarning('');
    }
    return {
      valid: true,
      city: data.city || '',
      state: expected,
      mismatch,
    };
  }, [token]);

  const clearWarning = useCallback(() => setWarning(''), []);

  return { resolve, warning, loading, clearWarning };
}
